import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@tinct/web-components';
import {
  createCustomCodeSchema,
  type CreateCustomCode,
  type CustomCodePlacement,
  type ReadCustomCode,
} from '@tinct/core-api-models';
import {
  customCodePlacementLabels,
  customCodePlacements,
} from './custom-code-placement';

export type CustomCodeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing entry to edit — null for a new one. */
  code: ReadCustomCode | null;
  /** Server-side rejection message (400 field validation). */
  errorMessage?: string | null;
  isSaving?: boolean;
  onSave: (data: CreateCustomCode) => void;
};

/**
 * Create/edit modal for one custom-code entry: name, placement, raw HTML
 * (injected verbatim — scripts, styles, JSON-LD…), and the enable toggle.
 */
export function CustomCodeModal({
  open,
  onOpenChange,
  code,
  errorMessage,
  isSaving,
  onSave,
}: CustomCodeModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        {/* Radix unmounts the content on close, so the form state resets
            naturally on every open — no reset effect needed. */}
        <CustomCodeForm
          code={code}
          errorMessage={errorMessage}
          isSaving={isSaving}
          onSave={onSave}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

type CustomCodeFormProps = {
  code: ReadCustomCode | null;
  errorMessage?: string | null;
  isSaving?: boolean;
  onSave: (data: CreateCustomCode) => void;
  onCancel: () => void;
};

function CustomCodeForm({
  code,
  errorMessage,
  isSaving,
  onSave,
  onCancel,
}: CustomCodeFormProps) {
  const [name, setName] = useState(code?.name ?? '');
  const [placement, setPlacement] = useState<CustomCodePlacement>(
    code?.placement ?? 'head_end',
  );
  const [codeValue, setCodeValue] = useState(code?.code ?? '');
  const [enabled, setEnabled] = useState(code?.enabled ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSave = () => {
    const parsed = createCustomCodeSchema.safeParse({
      name: name.trim(),
      placement,
      code: codeValue,
      enabled,
    });
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Invalid entry');
      return;
    }
    setValidationError(null);
    onSave(parsed.data);
  };

  const displayedError = validationError ?? errorMessage;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{code ? 'Edit custom code' : 'Add custom code'}</DialogTitle>
      </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <div className="flex flex-1 flex-col gap-2">
              <Label htmlFor="custom-code-name">Name</Label>
              <Input
                id="custom-code-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Google Tag Manager"
                maxLength={100}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Placement</Label>
              <Select
                value={placement}
                onValueChange={(value) => setPlacement(value as CustomCodePlacement)}
              >
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {customCodePlacements.map((value) => (
                    <SelectItem key={value} value={value}>
                      {customCodePlacementLabels[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="custom-code-code">Code</Label>
            <Textarea
              id="custom-code-code"
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value)}
              placeholder={'<script>\n  // …\n</script>'}
              spellCheck={false}
              className="min-h-56 font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Place{' '}
              <code className="rounded bg-muted px-1 py-0.5">&lt;script&gt;</code>{' '}
              tags in &lsquo;End of{' '}
              <code className="rounded bg-muted px-1 py-0.5">&lt;body&gt;</code>
              &rsquo; for faster page loading.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="custom-code-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
            <Label htmlFor="custom-code-enabled">Enabled</Label>
          </div>

          {displayedError && (
            <p className="text-sm text-destructive">{displayedError}</p>
          )}
        </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} isLoading={isSaving}>
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
